import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import './PaymentPorterPage.css';
import './PaymentPorterProcessorPage.css';
import paymentporterService from '../services/paymentporterService';
import corporateProfileService from '../services/corporateProfileService';
import iataProfileService from '../services/iataProfileService';
import rateCodeService from '../services/rateCodeService';
import paymentMethodService from '../services/paymentMethodService';
import profileAssignmentService from '../services/profileAssignmentService';
import routingService from '../services/routingService';
import ppProcessorService from '../services/ppProcessorService';
import ConfirmModal from './shared/ConfirmModal';
import OhipSimulateModal from './OhipSimulateModal';
import { useAuth } from '../contexts/AuthContext';

// ─── Criteria type definitions (the 3 selectable types) ──────────────────────

const CRITERIA_TYPES = [
    {
        key: 'corporate-profiles',
        icon: (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <rect x="4" y="3" width="16" height="18" rx="2" stroke="currentColor" strokeWidth="1.6"/>
                <path d="M8 7h8M8 11h8M8 15h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
        ),
        title: 'Corporate Profiles',
    },
    {
        key: 'profile-iata',
        icon: (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <circle cx="9" cy="7" r="3" stroke="currentColor" strokeWidth="1.6"/>
                <circle cx="17" cy="7" r="3" stroke="currentColor" strokeWidth="1.6"/>
                <path d="M3 19c0-3 2.5-5 6-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                <path d="M11 19c0-3 2.5-5 6-5s6 2 6 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
        ),
        title: 'Profile IATA',
    },
    {
        key: 'rate-codes',
        icon: (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="7" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.6"/>
                <circle cx="8" cy="14" r="1.5" stroke="currentColor" strokeWidth="1.4"/>
                <circle cx="16" cy="14" r="1.5" stroke="currentColor" strokeWidth="1.4"/>
                <path d="M5 7V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="1.6"/>
            </svg>
        ),
        title: 'Rate Codes',
    },
];

// ─── Criteria tile definitions ────────────────────────────────────────────────

const CRITERIA_TILES = (ppId, ruleId) => {
    const ruleQ = ruleId ? `?ruleId=${ruleId}` : '';
    return [
        {
            key: 'corporate-profiles',
            icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <rect x="4" y="3" width="16" height="18" rx="2" stroke="#4a6cf7" strokeWidth="1.6"/>
                    <path d="M8 7h8M8 11h8M8 15h5" stroke="#4a6cf7" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
            ),
            title: 'Corporate Profiles',
            addPath: `/apps/payment-porter/${ppId}/corporate-profiles/new${ruleQ}`,
            editPath: (item) => `/apps/payment-porter/${ppId}/corporate-profiles/${item.id}/edit${ruleQ}`,
            removeFn: (id, itemId) => corporateProfileService.remove(id, itemId),
            renderRow: (item) => (
                <>
                    <span className="pp-criteria-row-primary">{item.name || item.profile_id}</span>
                    {item.agent_type && <span className="pp-criteria-row-badge">{item.agent_type}</span>}
                    {item.profile_id && item.name && <span className="pp-criteria-row-meta">{item.profile_id}</span>}
                </>
            ),
        },
        {
            key: 'profile-iata',
            icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <circle cx="9" cy="7" r="3" stroke="#4a6cf7" strokeWidth="1.6"/>
                    <circle cx="17" cy="7" r="3" stroke="#4a6cf7" strokeWidth="1.6"/>
                    <path d="M3 19c0-3 2.5-5 6-5" stroke="#4a6cf7" strokeWidth="1.6" strokeLinecap="round"/>
                    <path d="M11 19c0-3 2.5-5 6-5s6 2 6 5" stroke="#4a6cf7" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
            ),
            title: 'Profile IATA',
            addPath: `/apps/payment-porter/${ppId}/profile-iata/new${ruleQ}`,
            editPath: (item) => `/apps/payment-porter/${ppId}/profile-iata/${item.id}/edit${ruleQ}`,
            removeFn: (id, itemId) => iataProfileService.remove(id, itemId),
            renderRow: (item) => (
                <>
                    <span className="pp-criteria-row-primary">{item.name || item.corp_id_iata}</span>
                    {item.profile_type && <span className="pp-criteria-row-badge">{item.profile_type}</span>}
                    {item.corp_id_iata && item.name && <span className="pp-criteria-row-meta">{item.corp_id_iata}</span>}
                </>
            ),
        },
        {
            key: 'rate-codes',
            icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <rect x="2" y="7" width="20" height="14" rx="2" stroke="#4a6cf7" strokeWidth="1.6"/>
                    <circle cx="8" cy="14" r="1.5" stroke="#4a6cf7" strokeWidth="1.4"/>
                    <circle cx="16" cy="14" r="1.5" stroke="#4a6cf7" strokeWidth="1.4"/>
                    <path d="M5 7V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v2" stroke="#4a6cf7" strokeWidth="1.6"/>
                </svg>
            ),
            title: 'Rate Codes',
            addPath: `/apps/payment-porter/${ppId}/rate-codes/new${ruleQ}`,
            editPath: (item) => `/apps/payment-porter/${ppId}/rate-codes/${item.id}/edit${ruleQ}`,
            removeFn: (id, itemId) => rateCodeService.remove(id, itemId),
            renderRow: (item) => (
                <>
                    <span className="pp-criteria-row-primary">{item.rate_code}</span>
                    {item.rate_code_name && <span className="pp-criteria-row-meta">{item.rate_code_name}</span>}
                </>
            ),
        },
        {
            key: 'payment-methods',
            icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <rect x="2" y="5" width="20" height="14" rx="2" stroke="#4a6cf7" strokeWidth="1.6"/>
                    <path d="M2 10h20" stroke="#4a6cf7" strokeWidth="1.6"/>
                    <path d="M6 15h4" stroke="#4a6cf7" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
            ),
            title: 'Payment Methods',
            addPath: `/apps/payment-porter/${ppId}/payment-methods/new${ruleQ}`,
            editPath: (item) => `/apps/payment-porter/${ppId}/payment-methods/${item.id}/edit${ruleQ}`,
            removeFn: (id, itemId) => paymentMethodService.remove(id, itemId),
            renderRow: (item) => (
                <>
                    <span className="pp-criteria-row-primary">{item.code}</span>
                    {item.description && <span className="pp-criteria-row-meta">{item.description}</span>}
                    {item.active === false && <span className="pp-criteria-row-badge" style={{ background: '#fee2e2', color: '#dc2626' }}>Inactive</span>}
                </>
            ),
        },
    ];
};

// ─── Main component ───────────────────────────────────────────────────────────

const PaymentPorterRulePage = () => {
    const navigate              = useNavigate();
    const { ppId, ruleId }      = useParams();
    const [searchParams]        = useSearchParams();
    const ruleIdNum             = parseInt(ruleId);
    const { isSuperAdmin }      = useAuth();
    // Seed for the shared OHIP request simulator (superadmin) — { method, url, headers } or null.
    const [simSeed, setSimSeed] = useState(null);

    const [pp, setPp]                     = useState(null);
    const [rule, setRule]                 = useState(null);
    const [loading, setLoading]           = useState(true);
    // Which parts of the rule failed to load. Each part loads on its own, so one failing endpoint shows as an error
    // instead of emptying the whole page into what looks like a blank create form.
    const [loadErrors, setLoadErrors]     = useState([]);
    const [confirmModal, setConfirmModal] = useState(null);
    const [criteria, setCriteria]         = useState({ corporateProfiles: [], iataProfiles: [], rateCodes: [], paymentMethods: [] });
    const [profileAssignments, setProfileAssignments] = useState([]);
    const [routingCodes, setRoutingCodes] = useState([]);
    const [deletingCriteria, setDeletingCriteria] = useState(null);
    const [preview, setPreview]           = useState(null);
    const [selectedType, setSelectedType] = useState(null);
    const [currentStep, setCurrentStep]   = useState(() => parseInt(searchParams.get('step') || '1', 10));

    const loadAll = async () => {
        setLoading(true);
        const parts = [
            ['payment porter',      () => paymentporterService.getById(ppId)],
            ['rules',               () => paymentporterService.listRules(ppId)],
            ['corporate profiles',  () => corporateProfileService.list(ppId, ruleIdNum)],
            ['IATA profiles',       () => iataProfileService.list(ppId, ruleIdNum)],
            ['rate codes',          () => rateCodeService.list(ppId, ruleIdNum)],
            ['payment methods',     () => paymentMethodService.list(ppId, ruleIdNum)],
            ['profile assignments', () => profileAssignmentService.list(ppId, ruleIdNum)],
            ['routing codes',       () => routingService.list(ppId, ruleIdNum)],
        ];
        const results = await Promise.allSettled(parts.map(([, load]) => load()));
        const data = (i) => (results[i].status === 'fulfilled' ? results[i].value.data : null);
        const failed = results.map((r, i) => (r.status === 'rejected'
            ? `${parts[i][0]} (${r.reason?.response?.data?.error?.message || r.reason?.response?.data?.message || r.reason?.message || 'failed'})`
            : null)).filter(Boolean);
        if (failed.length) console.error('Failed to load parts of the rule page:', failed);
        setLoadErrors(failed);
        setPp(data(0)?.paymentPorter || null);
        setRule((data(1)?.rules || []).find(r => r.id === ruleIdNum) || null);
        setCriteria({
            corporateProfiles: data(2)?.profiles   || [],
            iataProfiles:      data(3)?.profiles   || [],
            rateCodes:         data(4)?.rateCodes  || [],
            paymentMethods:    data(5)?.methods    || [],
        });
        setProfileAssignments(data(6)?.profileAssignments || []);
        setRoutingCodes(data(7)?.routes || []);
        setLoading(false);
    };

    const loadPreview = async () => {
        setPreview('loading');
        try {
            const res = await ppProcessorService.getCriteriaPreview(ppId, ruleIdNum);
            setPreview(res.data || { available: false });
        } catch {
            setPreview({ available: false, reason: 'Failed to load' });
        }
    };

    useEffect(() => { loadAll(); }, [ppId, ruleId]); // eslint-disable-line
    useEffect(() => { if (currentStep === 3) loadPreview(); }, [currentStep]); // eslint-disable-line

    const handleDeleteCriteria = (tile, itemId) => {
        setConfirmModal({
            title: `Remove ${tile.title}`,
            message: 'Remove this item from the rule criteria?',
            confirmLabel: 'Remove',
            danger: true,
            onConfirm: async () => {
                setDeletingCriteria({ tileKey: tile.key, itemId });
                try {
                    await tile.removeFn(ppId, itemId);
                    await loadAll();
                } catch (err) {
                    console.error('Failed to delete criteria item', err);
                } finally {
                    setDeletingCriteria(null);
                }
            },
        });
    };

    const handleDeleteRouting = (item) => {
        setConfirmModal({
            title: 'Remove Routing Code',
            message: `Remove routing code "${item.code}" from this rule? The routing code itself will not be deleted.`,
            confirmLabel: 'Remove',
            danger: true,
            onConfirm: async () => {
                try {
                    await routingService.remove(ppId, item.id);
                    setRoutingCodes(prev => prev.filter(r => r.id !== item.id));
                } catch (err) {
                    console.error('Failed to remove routing code', err);
                }
            },
        });
    };

    const criteriaData = {
        'corporate-profiles': criteria.corporateProfiles,
        'profile-iata':       criteria.iataProfiles,
        'rate-codes':         criteria.rateCodes,
        'payment-methods':    criteria.paymentMethods,
    };

    const activeType = criteria.corporateProfiles.length > 0 ? 'corporate-profiles'
                     : criteria.iataProfiles.length > 0      ? 'profile-iata'
                     : criteria.rateCodes.length > 0         ? 'rate-codes'
                     : null;
    const resolvedType = activeType || selectedType;
    const ruleQ = ruleIdNum ? `?ruleId=${ruleIdNum}` : '';

    if (loading) {
        return <div className="pp-page" style={{ color: '#6b7280', paddingTop: '3rem' }}>Loading...</div>;
    }

    const fmt = (n, more) => more ? `${n}+` : String(n);
    const isPreviewLoading     = preview === 'loading' || preview === null;
    const isPreviewUnavailable = !isPreviewLoading && !preview.available;

    const PreviewSpinner = () => (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="pp-preview-spinner">
            <circle cx="12" cy="12" r="9" stroke="#c7d2fe" strokeWidth="2.5"/>
            <path d="M12 3a9 9 0 0 1 9 9" stroke="#4a6cf7" strokeWidth="2.5" strokeLinecap="round"/>
        </svg>
    );

    const CheckIcon = () => (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );

    const stepClass = (n) => {
        if (currentStep === n) return 'pp-wstep-active';
        if (currentStep > n)  return 'pp-wstep-done';
        return 'pp-wstep-future';
    };

    return (
        <div className="pp-page">
            {/* Breadcrumb header */}
            <div className="pp-header">
                <button className="pp-back-btn" onClick={() => navigate(`/apps/payment-porter/${ppId}`)}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {pp?.name || 'Payment Porter'}
                </button>
                <div className="pp-title-row">
                    <div className="pp-title-icon" style={{ background: '#f0fdf4', color: '#16a34a' }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                            <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.75"/>
                            <path d="M8 12l3 3 5-5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                    <div>
                        <h1 className="pp-title">{rule?.name || 'Rule'}</h1>
                        {pp?.name && <p className="pp-subtitle-text">{pp.hotel_name || pp.account_name || pp.name}</p>}
                        <span className={`pp-rule-page-badge ${rule?.active ? 'pp-rule-page-badge-active' : 'pp-rule-page-badge-inactive'}`}>
                            {rule?.active ? 'Active' : 'Inactive'}
                        </span>
                        {/* Fixed at creation — a VCC rule can never become DB, nor the reverse. */}
                        <span className={`pp-rule-type-badge pp-rule-type-${(rule?.rule_type || 'VCC').toLowerCase()}`}
                            style={{ marginLeft: '0.4rem' }}
                            title="The rule type is set when the rule is created and cannot be changed">
                            {(rule?.rule_type || 'VCC') === 'DB' ? 'Direct Bill (DB)' : 'Virtual Credit Card (VCC)'}
                        </span>
                    </div>
                </div>
            </div>

            {loadErrors.length > 0 && (
                <div className="ppp-error" role="alert" style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '0.75rem 1rem', margin: '0 0 1rem' }}>
                    Part of this rule could not be loaded, so what is shown below is incomplete — do not edit it until this
                    is fixed: {loadErrors.join('; ')}.
                </div>
            )}

            {/* Step indicator */}
            <div className="pp-wizard-stepper pp-rule-stepper">
                <div className={`pp-wizard-step ${stepClass(1)}`}>
                    <span className="pp-wizard-step-circle">{currentStep > 1 ? <CheckIcon/> : '1'}</span>
                    <span className="pp-wizard-step-label">Criteria</span>
                </div>
                <div className={`pp-wizard-step-connector${currentStep > 1 ? ' pp-wconn-done' : ''}`}/>
                <div className={`pp-wizard-step ${stepClass(2)}`}>
                    <span className="pp-wizard-step-circle">{currentStep > 2 ? <CheckIcon/> : '2'}</span>
                    <span className="pp-wizard-step-label">Actions</span>
                </div>
                <div className={`pp-wizard-step-connector${currentStep > 2 ? ' pp-wconn-done' : ''}`}/>
                <div className={`pp-wizard-step ${stepClass(3)}`}>
                    <span className="pp-wizard-step-circle">3</span>
                    <span className="pp-wizard-step-label">Test</span>
                </div>
            </div>

            {/* ── Step 1: Criteria ── */}
            {currentStep === 1 && (
                <div>
                    <div className="pp-step-heading">
                        <h2 className="pp-step-title">Define Reservation Selection Criteria</h2>
                        <p className="pp-step-subtitle">
                            {resolvedType
                                ? 'Add criteria items, then add a payment method to complete this step'
                                : <><span style={{color:'#dc2626', marginRight:'4px'}}>*</span>Select a criteria type to continue</>}
                        </p>
                    </div>

                    {/* Type selector */}
                    <div className="pp-criteria-type-grid">
                        {CRITERIA_TYPES.map(t => (
                            <button
                                key={t.key}
                                className={`pp-criteria-type-card${resolvedType === t.key ? ' pp-criteria-type-card-selected' : ''}${activeType && activeType !== t.key ? ' pp-criteria-type-card-disabled' : ''}`}
                                onClick={() => !activeType && setSelectedType(t.key)}
                                disabled={!!activeType && activeType !== t.key}
                            >
                                <span className="pp-criteria-type-card-icon">{t.icon}</span>
                                <span className="pp-criteria-type-card-title">{t.title}</span>
                                {resolvedType === t.key && (
                                    <span className="pp-criteria-type-card-check">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Criteria cards — shown once a type is selected */}
                    {resolvedType && (
                        <div className="pp-criteria-list" style={{ marginTop: '16px' }}>
                            {/* Selected criteria type */}
                            {(() => {
                                const tile  = CRITERIA_TILES(ppId, ruleIdNum).find(t => t.key === resolvedType);
                                const items = criteriaData[resolvedType] || [];
                                return (
                                    <div className="pp-criteria-card">
                                        <div className="pp-criteria-card-header">
                                            <div className="pp-criteria-card-label">
                                                <span className="pp-criteria-card-icon">{tile.icon}</span>
                                                {tile.title}
                                            </div>
                                            <button className="pp-criteria-add-btn pp-criteria-add-btn-primary" onClick={() => navigate(tile.addPath)}>
                                                <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                                                Add
                                            </button>
                                        </div>
                                        {items.length === 0 ? (
                                            <div className="pp-criteria-empty">No {tile.title.toLowerCase()} configured</div>
                                        ) : (
                                            items.map(item => {
                                                const isDeleting = deletingCriteria?.tileKey === tile.key && deletingCriteria?.itemId === item.id;
                                                return (
                                                    <div key={item.id} className="pp-criteria-row">
                                                        <div className="pp-criteria-row-content">{tile.renderRow(item)}</div>
                                                        <div className="pp-criteria-row-actions">
                                                            <button className="pp-criteria-add-btn pp-criteria-add-btn-primary pp-criteria-add-btn-icon" title="Edit" onClick={() => navigate(tile.editPath(item))} disabled={isDeleting}>
                                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                                            </button>
                                                            <button className="pp-criteria-add-btn pp-criteria-add-btn-danger pp-criteria-add-btn-icon" title="Delete" onClick={() => handleDeleteCriteria(tile, item.id)} disabled={isDeleting}>
                                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                );
                            })()}

                            {/* Payment Methods */}
                            {(() => {
                                const tile  = CRITERIA_TILES(ppId, ruleIdNum).find(t => t.key === 'payment-methods');
                                const items = criteriaData['payment-methods'] || [];
                                return (
                                    <div className="pp-criteria-card">
                                        <div className="pp-criteria-card-header">
                                            <div className="pp-criteria-card-label">
                                                <span className="pp-criteria-card-icon">{tile.icon}</span>
                                                {tile.title}
                                            </div>
                                            <button className="pp-criteria-add-btn pp-criteria-add-btn-primary" onClick={() => navigate(tile.addPath)}>
                                                <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                                                Add
                                            </button>
                                        </div>
                                        {items.length === 0 ? (
                                            <div className="pp-criteria-empty">No payment methods configured</div>
                                        ) : (
                                            items.map(item => {
                                                const isDeleting = deletingCriteria?.tileKey === 'payment-methods' && deletingCriteria?.itemId === item.id;
                                                return (
                                                    <div key={item.id} className="pp-criteria-row">
                                                        <div className="pp-criteria-row-content">{tile.renderRow(item)}</div>
                                                        <div className="pp-criteria-row-actions">
                                                            <button className="pp-criteria-add-btn pp-criteria-add-btn-primary pp-criteria-add-btn-icon" title="Edit" onClick={() => navigate(tile.editPath(item))} disabled={isDeleting}>
                                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                                            </button>
                                                            <button className="pp-criteria-add-btn pp-criteria-add-btn-danger pp-criteria-add-btn-icon" title="Delete" onClick={() => handleDeleteCriteria(tile, item.id)} disabled={isDeleting}>
                                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                </div>
            )}

            {/* ── Step 2: Actions ── */}
            {currentStep === 2 && (
                <div>
                    <div className="pp-step-heading">
                        <h2 className="pp-step-title">Actions</h2>
                        <p className="pp-step-subtitle">Configure what happens to reservations that match the selection criteria</p>
                    </div>
                    {rule?.rule_type === 'DB' && (
                        <div className="pp-preview-unavailable" style={{ marginBottom: '1rem' }}>
                            Direct Bill rules can be configured now but are not processed yet — runs skip them, so nothing is sent to OPERA.
                        </div>
                    )}
                    <div className="pp-criteria-list">
                        {/* Assign to Profile */}
                        <div className="pp-criteria-card">
                            <div className="pp-criteria-card-header">
                                <div className="pp-criteria-card-label">
                                    <span className="pp-criteria-card-icon">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                            <circle cx="12" cy="8" r="4" stroke="#4a6cf7" strokeWidth="1.6"/>
                                            <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="#4a6cf7" strokeWidth="1.6" strokeLinecap="round"/>
                                        </svg>
                                    </span>
                                    {rule?.rule_type === 'DB' ? 'Bill to AR Account' : 'Assign to Profile'}
                                </div>
                                {profileAssignments.length === 0 && (
                                    <button className="pp-criteria-add-btn pp-criteria-add-btn-primary" onClick={() => navigate(`/apps/payment-porter/${ppId}/profile-assignments/new?ruleId=${ruleIdNum}&returnStep=2`)}>
                                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                                            <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                        </svg>
                                        Add
                                    </button>
                                )}
                            </div>
                            {profileAssignments.length === 0 ? (
                                <div className="pp-criteria-empty">No profile assignments configured</div>
                            ) : (
                                profileAssignments.map(item => (
                                    <div key={item.id} className="pp-criteria-row">
                                        <div className="pp-criteria-row-content">
                                            <span className="pp-criteria-row-primary">{item.profile_name || item.profile_id}</span>
                                            {item.profile_type && <span className="pp-criteria-row-badge">{item.profile_type}</span>}
                                            {item.profile_id && item.profile_name && <span className="pp-criteria-row-meta">{item.profile_id}</span>}
                                            {item.ar_account_no && <span className="pp-criteria-row-meta">AR {item.ar_account_no}</span>}
                                        </div>
                                        <div className="pp-criteria-row-actions">
                                            <button className="pp-criteria-add-btn pp-criteria-add-btn-primary pp-criteria-add-btn-icon" title="Edit" onClick={() => navigate(`/apps/payment-porter/${ppId}/profile-assignments/${item.id}/edit?ruleId=${ruleIdNum}&returnStep=2`)}>
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Routing Code */}
                        <div className="pp-criteria-card">
                            <div className="pp-criteria-card-header">
                                <div className="pp-criteria-card-label">
                                    <span className="pp-criteria-card-icon">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                            <circle cx="7" cy="7" r="3" stroke="#4a6cf7" strokeWidth="1.6"/>
                                            <circle cx="17" cy="17" r="3" stroke="#4a6cf7" strokeWidth="1.6"/>
                                            <path d="M7 10v4a3 3 0 0 0 3 3h4" stroke="#4a6cf7" strokeWidth="1.6" strokeLinecap="round"/>
                                        </svg>
                                    </span>
                                    {rule?.rule_type === 'DB' ? 'Routing Code (City Ledger)' : 'Routing Code'}
                                </div>
                                <button className="pp-criteria-add-btn pp-criteria-add-btn-primary" onClick={() => navigate(`/apps/payment-porter/${ppId}/routing/new?ruleId=${ruleIdNum}&returnStep=2`)}>
                                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                                        <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    </svg>
                                    Add
                                </button>
                            </div>
                            {routingCodes.length === 0 ? (
                                <div className="pp-criteria-empty">No routing codes configured</div>
                            ) : (
                                routingCodes.map(item => (
                                    <div key={item.id} className="pp-criteria-row">
                                        <div className="pp-criteria-row-content">
                                            <span className="pp-criteria-row-primary">{item.code}</span>
                                            {item.description && <span className="pp-criteria-row-meta">{item.description}</span>}
                                        </div>
                                        <div className="pp-criteria-row-actions">
                                            <button className="pp-criteria-add-btn pp-criteria-add-btn-primary pp-criteria-add-btn-icon" title="Edit" onClick={() => navigate(`/apps/payment-porter/${ppId}/routing/${item.id}/edit?ruleId=${ruleIdNum}&returnStep=2`)}>
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                            </button>
                                            <button className="pp-criteria-add-btn pp-criteria-add-btn-danger pp-criteria-add-btn-icon" title="Delete" onClick={() => handleDeleteRouting(item)}>
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Step 3: Test ── */}
            {currentStep === 3 && (
                <div>
                    <div className="pp-step-heading">
                        <h2 className="pp-step-title">Test</h2>
                        <p className="pp-step-subtitle">Preview how many reservations match your criteria against today's data</p>
                    </div>
                    <div className="pp-criteria-list">
                        <div className="pp-preview-header-row">
                            <button className="pp-refresh-btn" onClick={loadPreview} disabled={isPreviewLoading} title="Refresh">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                    <path d="M4 4v6h6M20 20v-6h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M20 9A9 9 0 0 0 5.64 5.64L4 10M4 15a9 9 0 0 0 14.36 3.36L20 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                Refresh
                            </button>
                            {isSuperAdmin && preview?.queries?.step1_list?.length > 0 && (
                                <button
                                    className="pp-refresh-btn sa-only"
                                    onClick={() => {
                                        const q = preview.queries.step1_list[0];
                                        setSimSeed({ method: q.method || 'GET', url: q.url, headers: {} });
                                    }}
                                    title={preview.queries.step1_list.length > 1
                                        ? `Open the OHIP query in the simulator (seeds the first of ${preview.queries.step1_list.length} axis queries)`
                                        : 'Open the OHIP query that finds matching reservations in the simulator'}
                                >
                                    Simulate OHIP query
                                </button>
                            )}
                        </div>
                        {isPreviewLoading ? (
                            <div className="pp-preview-loading"><PreviewSpinner/>{preview === null ? 'Loading…' : 'Fetching from OHIP…'}</div>
                        ) : isPreviewUnavailable ? (
                            <div className="pp-preview-unavailable">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="#9ca3af" strokeWidth="1.8"/><path d="M12 8v4M12 16h.01" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round"/></svg>
                                {preview.reason || 'Unavailable — configure OHIP credentials in Settings to enable'}
                            </div>
                        ) : (
                            <>
                                <div className="pp-preview-date" style={{ marginBottom: '0.75rem' }}>OHIP business date · {preview.businessDate}</div>
                                <table className="pp-test-table">
                                    <thead>
                                        <tr><th>Metric</th><th>Count</th><th></th></tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Total Reservations</td>
                                            <td className="pp-test-table-num">{fmt(preview.total, preview.totalHasMore)}</td>
                                            <td></td>
                                        </tr>
                                        <tr>
                                            <td>Reservations Matching Criteria</td>
                                            <td className="pp-test-table-num">
                                                {preview.hasCriteria ? fmt(preview.matched, preview.matchedHasMore) : '—'}
                                            </td>
                                            <td>
                                                {preview.hasCriteria && (
                                                    <button className="ppp-detail-btn ppp-detail-btn-primary" onClick={() => navigate(`/apps/payment-porter/${ppId}/rules/${ruleId}/matched-reservations`)}>
                                                        <svg width="11" height="11" viewBox="0 0 16 16" fill="none" style={{flexShrink:0}}><path d="M2 4h12M2 8h8M2 12h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                                                        Details
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Navigation footer */}
            <div className="pp-rule-nav-footer">
                {currentStep > 1 && (
                    <button className="pp-rule-nav-btn pp-rule-nav-btn-back" onClick={() => setCurrentStep(s => s - 1)}>
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        Back
                    </button>
                )}
                {currentStep < 3 && (
                    <button
                        className="pp-rule-nav-btn pp-rule-nav-btn-next"
                        onClick={() => setCurrentStep(s => s + 1)}
                        disabled={currentStep === 1 && !resolvedType}
                    >
                        Next
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </button>
                )}
                {currentStep === 3 && (
                    <button className="pp-rule-nav-btn pp-rule-nav-btn-done" onClick={() => navigate(`/apps/payment-porter/${ppId}`)}>
                        Done
                    </button>
                )}
            </div>

            {confirmModal && (
                <ConfirmModal
                    title={confirmModal.title}
                    message={confirmModal.message}
                    confirmLabel={confirmModal.confirmLabel}
                    danger={confirmModal.danger}
                    onConfirm={() => { confirmModal.onConfirm(); setConfirmModal(null); }}
                    onCancel={() => setConfirmModal(null)}
                />
            )}

            {simSeed && isSuperAdmin && (
                <OhipSimulateModal
                    seed={simSeed}
                    onClose={() => setSimSeed(null)}
                    sim={{
                        simulate:  (request) => ppProcessorService.ohipSimulate(ppId, request),
                        loadToken: ()        => ppProcessorService.ohipSimulate(ppId, { tokenOnly: true }),
                        curlPath:  `/paymentporter/${ppId}/ohip-simulate`,
                        description: <>Sent through this payment porter’s <strong>OHIP context</strong> (its configured connector credentials). This is the OHIP list query that finds matching reservations — edit and re-send to test it.</>,
                    }}
                />
            )}
        </div>
    );
};

export default PaymentPorterRulePage;
